import { resolveGamesOrigin } from "./gameStore.ts";

/**
 * A Suno link, turned into the track behind it.
 *
 * This is the whole difference between making a game and giving up on it. Somebody who has just
 * written a song has a link to it and nothing else, and asking them to download the file, find
 * somewhere that will serve it, and dig out a direct address is three chances to stop bothering.
 *
 * It works because Suno serves the file itself with the door open: measured, `audio/mpeg`,
 * range requests, and `access-control-allow-origin: *`. So a browser may not only play it but
 * read it, which means the track can be analysed here without ever being downloaded by hand.
 * Nothing is copied and nothing is stored; a game keeps the same address anybody else would
 * follow.
 *
 * A song page carries its own id in the address, so that case needs nothing from the network.
 * A short link is a redirect and only the far end knows where it goes, so that one is asked.
 */

const SONG = /^\/song\/([0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12})$/i;
const SHARE = /^\/s\/[A-Za-z0-9]{6,32}$/;
const PROFILE = /^\/@[A-Za-z0-9_.-]{1,40}$/;
const FILE = /^https:\/\/cdn1\.suno\.ai\/([0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12})\.mp3$/i;

/** What kind of thing somebody pasted, or nothing if it is not Suno's at all. */
export type SunoKind = "song" | "file" | "profile";

export function sunoKind(value: string): SunoKind | null {
  const trimmed = value.trim();
  if (FILE.test(trimmed)) return "file";
  try {
    const url = new URL(trimmed);
    if (url.protocol !== "https:") return null;
    const host = url.hostname.toLowerCase();
    if (host !== "suno.com" && host !== "www.suno.com") return null;
    if (SONG.test(url.pathname) || SHARE.test(url.pathname)) return "song";
    if (PROFILE.test(url.pathname)) return "profile";
    return null;
  } catch {
    return null;
  }
}

/** A track's own id, which is the same forever and is what a made game is filed under. */
export function sunoSongId(audioUrl: string): string | null {
  return FILE.exec(audioUrl)?.[1]?.toLowerCase() ?? null;
}

export interface SunoTrack {
  readonly audio: string;
  readonly title: string;
  /** Their own picture for the track, when the page still offers one. */
  readonly art?: string;
  /** What the track was asked to sound like. Written before the song, so better than a guess. */
  readonly style?: string;
  /** Whoever made the song, by their Suno handle. Not whoever makes a game out of it. */
  readonly artist?: string;
  /** The words, when it has any. Text only: this is never safe in an attribute. */
  readonly lyrics?: string;
  /** Length in seconds, known before a note of it has been listened to. */
  readonly seconds?: number;
}

export function isSunoLink(value: string): boolean {
  return sunoKind(value) !== null;
}

export async function resolveSuno(value: string): Promise<SunoTrack> {
  const trimmed = value.trim();

  /*
   * Everything goes to the server, including links that already name the file.
   *
   * This used to answer a song link from the address alone, on the grounds that the file name is
   * all a game needs to play. That was true when a game was a chart and an address, and quietly
   * stopped being true the day the shelf started showing a picture, a genre and a maker: those
   * live on the page and nowhere else, so short-circuiting meant every game made from a Suno
   * link arrived with none of them. The whole catalogue was blank and nothing had failed.
   *
   * A bare file address is turned back into its page for the same reason. If the page cannot be
   * read the track still plays, because the address was always enough for that much.
   */
  let asking = trimmed;
  const file = FILE.exec(trimmed);
  if (file !== null) {
    const song = /([0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12})/i.exec(trimmed);
    if (song === null) return { audio: trimmed, title: "" };
    asking = `https://suno.com/song/${song[1]!.toLowerCase()}`;
  }

  try {
    new URL(asking);
  } catch {
    throw new Error("That does not look like a Suno link.");
  }

  const response = await fetch(`${resolveGamesOrigin()}/resolve?url=${encodeURIComponent(asking)}`);
  if (!response.ok) {
    // A page that will not open still leaves a playable track, when the address named the file.
    if (file !== null) return { audio: trimmed, title: "" };
    throw new Error(
      response.status === 404
        ? "There is no track at that link."
        : "That Suno link could not be opened. Try the address of the song page itself.",
    );
  }
  const body = (await response.json()) as {
    audio?: unknown;
    title?: unknown;
    art?: unknown;
    seconds?: unknown;
    style?: unknown;
    artist?: unknown;
    lyrics?: unknown;
  };
  if (typeof body.audio !== "string" || !body.audio.startsWith("https://cdn1.suno.ai/")) {
    throw new Error("That Suno link did not lead to a track.");
  }
  const said = (value: unknown): string => (typeof value === "string" ? value : "");
  return {
    audio: body.audio,
    title: said(body.title),
    art: said(body.art),
    style: said(body.style),
    artist: said(body.artist),
    lyrics: said(body.lyrics),
    seconds: typeof body.seconds === "number" ? body.seconds : 0,
  };
}

/** Everything on somebody's profile page, in the order the page itself lists it. */
export async function resolveCatalog(value: string): Promise<SunoTrack[]> {
  const response = await fetch(
    `${resolveGamesOrigin()}/catalog?url=${encodeURIComponent(value.trim())}`,
  );
  if (!response.ok) throw new Error("That profile could not be opened.");

  const body = (await response.json()) as { tracks?: unknown };
  if (!Array.isArray(body.tracks)) return [];

  const out: SunoTrack[] = [];
  for (const entry of body.tracks) {
    if (typeof entry !== "object" || entry === null) continue;
    const row = entry as Record<string, unknown>;
    if (typeof row.audio !== "string" || sunoSongId(row.audio) === null) continue;
    out.push({
      audio: row.audio,
      title: typeof row.title === "string" ? row.title.slice(0, 80) : "",
    });
  }
  return out;
}
